/**
 * Store (F5) console API (uc-core src/modules/store/controllers):
 *   /locations GET ?siteId&parentId&level&kind&groupId&active&search · POST · PATCH /:id · DELETE /:id
 *   /locations/:id · /:id/tree · /:id/contents · /:id/declaration · /by-qr/:payload
 *   /locations/:id/members GET · POST { userId } · DELETE /:userId · /locations/:id/count-requests POST { note? }
 *   /locations/count-requests GET ?status=OPEN|DONE|ALL&siteId&locationId
 *   /stock GET ?cropId&stream&siteId&locationId&hold&available&unitType&placeKind&farmerId&search
 *   /stock/lookup ?code|tag|farmerId · /stock/red-tie ?siteId · /stock/dock ?siteId
 *   /units/:id/custody
 *   /shipments GET ?status&kind&fromSiteId&toSiteId&siteId&search&dateFrom&dateTo · /shipments/manifests · /shipments/:id
 *   /samples GET ?unitId&kind&status&locationId&siteId · POST /samples/:id/dispatch { labOrgId, panelRequested, sentAt, dispatchRef? }
 *   /declarations/bays ?siteId&locationId · /declarations/bays/out-of-date ?siteId
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { getList, getOne, getPage, useConsoleMutation, usePageQuery, type PageParams } from './core'
import { ck } from './keys'
import { arr, bool, boolOrNull, iso, kgToGrams, kgToGrams0, num, numOrNull, obj, reasons, str, strOrNull } from './mappers'
import { mapExceptionLite, type ExceptionLite } from './gate'
import { HOLD_REASONS, humanize } from '../labels'

// ============================== locations ==============================

export const LOCATION_LEVELS = ['SITE', 'ZONE', 'BAY', 'POSITION'] as const
export const LOCATION_KINDS = ['COLLECTION_POINT', 'WAREHOUSE', 'PROCESSING', 'PACKING', 'LAB'] as const
export const LOCATION_ROLES = ['STORAGE', 'CONVENTIONAL', 'HOLD_AREA', 'SAMPLE_STORE', 'DOCK'] as const
export const LEVEL_LABEL: Record<string, string> = { SITE: 'Site', ZONE: 'Zone', BAY: 'Bay', POSITION: 'Spot' }
export const LOCATION_KIND_LABEL: Record<string, string> = {
  COLLECTION_POINT: 'Collection point',
  WAREHOUSE: 'Warehouse',
  PROCESSING: 'Processing unit',
  PACKING: 'Packing unit',
  LAB: 'Lab',
}
/** What a place is used for; Hindi as the keeper's phone shows it. */
export const LOCATION_ROLE_LABEL: Record<string, { en: string; hi: string }> = {
  STORAGE: { en: 'Storage', hi: 'भंडार' },
  CONVENTIONAL: { en: 'Ordinary (red tie)', hi: 'लाल-टाई बे' },
  HOLD_AREA: { en: 'Hold area', hi: 'रोक वाली जगह' },
  SAMPLE_STORE: { en: 'Sample store', hi: 'सैंपल स्टोर' },
  DOCK: { en: 'Dock', hi: 'डॉक' },
}

/** The bay declaration carried on a bay (tree) or a place (GET /locations/:id, by-qr). */
export interface LocationDeclaration {
  id: string
  formCode: string
  formVersion: number
  answers: Record<string, unknown>
  signedById: string | null
  signedAt: string | null
  validTo: string | null
  photo: string | null
  clean: boolean
  inForce: boolean
}
const mapLocationDeclaration = (d: Record<string, unknown>): LocationDeclaration => ({
  id: str(d.id),
  formCode: str(d.form_code),
  formVersion: num(d.form_version, 1),
  answers: obj(d.answers),
  signedById: strOrNull(d.signed_by_id),
  signedAt: iso(d.signed_at),
  validTo: strOrNull(d.valid_to),
  photo: strOrNull(d.photo_url ?? d.photo_id),
  clean: bool(d.clean),
  inForce: bool(d.inForce ?? d.in_force),
})

export interface LocationRow {
  id: string
  parentId: string | null
  level: string
  kind: string | null
  code: string
  name: string
  groupId: string | null
  active: boolean
  role: string | null
  capacityKg: number | null
  /** Not on tree nodes. */
  siteId: string | null
  /** Not on tree nodes (falls back to the code). */
  path: string
  labelPayload: string | null
  /** List: bags and samples here and below. Tree: here only. */
  itemsHere: number
  bayId: string | null
  /** Bays (tree) and single places: the latest declaration, null when never declared. */
  declaration: LocationDeclaration | null
  /** A clean declaration is in force (single place / by-qr: also for a spot, via its bay). */
  declaredClean: boolean | null
  validTo: string | null
  /** Sites: FSSAI licence (not on tree nodes). */
  fssaiLicenceNo: string | null
  /** YYYY-MM-DD */
  fssaiLicenceValidTo: string | null
  children?: LocationRow[]
}
export const mapLocation = (r: Record<string, unknown>): LocationRow => {
  const decl = r.declaration ? mapLocationDeclaration(obj(r.declaration)) : null
  return {
    id: str(r.id),
    parentId: strOrNull(r.parent_id ?? r.parentId),
    level: str(r.level),
    kind: strOrNull(r.kind),
    code: str(r.code),
    name: str(r.name),
    groupId: strOrNull(r.group_id ?? r.groupId),
    active: r.active === undefined ? true : bool(r.active),
    role: strOrNull(r.role),
    capacityKg: numOrNull(r.capacity_kg ?? r.capacityKg),
    siteId: strOrNull(r.site_id ?? r.siteId),
    path: str(r.path, str(r.code)),
    labelPayload: strOrNull(r.label_payload ?? r.labelPayload),
    itemsHere: num(r.items_here),
    bayId: strOrNull(r.bay_id),
    declaration: decl,
    declaredClean: r.declared_clean !== undefined ? boolOrNull(r.declared_clean) : decl ? decl.clean && decl.inForce : null,
    validTo: strOrNull(r.valid_to) ?? decl?.validTo ?? null,
    fssaiLicenceNo: strOrNull(r.fssai_licence_no ?? r.fssaiLicenceNo),
    fssaiLicenceValidTo: strOrNull(r.fssai_licence_valid_to ?? r.fssaiLicenceValidTo)?.slice(0, 10) ?? null,
    children: Array.isArray(r.children) ? arr(r.children).map(mapLocation) : undefined,
  }
}

export interface LocationFilters extends PageParams {
  siteId?: string
  parentId?: string
  level?: string
  kind?: string
  groupId?: string
  active?: boolean
  search?: string
}
export function useLocations(f: LocationFilters, enabled = true) {
  return usePageQuery([...ck.locations, 'list', f], () => getPage('/locations', f, mapLocation), { enabled })
}
export function useSites() {
  return useQuery({
    queryKey: [...ck.locations, 'sites'],
    queryFn: () => getPage('/locations', { level: 'SITE', page: 1, limit: 200 }, mapLocation).then((p) => p.rows),
    staleTime: 5 * 60_000,
  })
}
/** GET /locations/:id/tree — the whole site (any place of it works), nested in `children`. */
export function useLocationTree(siteId: string | null) {
  return useQuery({
    queryKey: [...ck.locations, 'tree', siteId],
    enabled: Boolean(siteId),
    queryFn: () => getOne(`/locations/${siteId}/tree`, mapLocation),
  })
}
/** One place with its path, site and (bay or spot) declaration. */
export function useLocation(id: string | null) {
  return useQuery({
    queryKey: [...ck.locations, 'one', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/locations/${id}`, mapLocation),
  })
}
export function lookupLocationLabel(payload: string) {
  return getOne(`/locations/by-qr/${encodeURIComponent(payload.trim())}`, mapLocation)
}

export interface LocationContents {
  lots: Array<{ unitId: string; unitCode: string; unitType: string; stream: string; hold: string; holdReasons: string[]; bagsHere: number; bagsTotal: number; bagsOnHold: number }>
  samples: Array<{ sampleId: string; kind: string; status: string; unitId: string | null; sealPayload: string | null }>
}
export function useLocationContents(id: string | null) {
  return useQuery({
    queryKey: [...ck.locations, 'contents', id],
    enabled: Boolean(id),
    queryFn: () =>
      getOne(`/locations/${id}/contents`, (r): LocationContents => ({
        lots: arr(r.lots).map((l) => ({
          unitId: str(l.unit_id),
          unitCode: str(l.unit_code),
          unitType: str(l.unit_type),
          stream: str(l.stream),
          hold: str(l.hold, 'NONE'),
          holdReasons: reasons(l.hold_reason),
          bagsHere: num(l.bags_here),
          bagsTotal: num(l.bags_total),
          bagsOnHold: num(l.bags_on_hold),
        })),
        samples: arr(r.samples).map((s) => ({
          sampleId: str(s.sample_id),
          kind: str(s.kind),
          status: str(s.status),
          unitId: strOrNull(s.unit_id),
          sealPayload: strOrNull(s.seal_payload),
        })),
      })),
  })
}

export interface LocationBody {
  parentId?: string
  level?: string
  kind?: string
  code?: string
  name?: string
  role?: string
  capacityKg?: number
  groupId?: string
  labelPayload?: string
  active?: boolean
  /** null clears it (edit only). */
  fssaiLicenceNo?: string | null
  fssaiLicenceValidTo?: string | null
}
export function useSaveLocation() {
  return useConsoleMutation<{ id?: string; body: LocationBody }, Record<string, unknown>>({
    mutationFn: ({ id, body }) => (id ? api.patch(`/locations/${id}`, body) : api.post('/locations', body)),
    invalidate: [ck.locations, ck.declarations],
    success: (d, v) => (v.id ? 'Place saved' : `Place created — label ${str(obj(d).label_payload, '')}`),
    errorTitle: 'Place not saved',
  })
}
/** `onError` returning true keeps the 409 ("still holds n bags") inline instead of a toast. */
export function useDeactivateLocation(onError?: (e: unknown) => boolean | void) {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.delete(`/locations/${id}`),
    invalidate: [ck.locations, ck.declarations],
    success: 'Place deactivated',
    errorTitle: 'Not deactivated',
    onError,
  })
}
export interface CountRequest {
  id: string
  locationId: string
  locationCode: string | null
  locationName: string | null
  path: string | null
  siteId: string | null
  requestedAt: string | null
  note: string | null
  requestedById: string | null
  requestedByName: string | null
  status: 'OPEN' | 'DONE'
  countId: string | null
  countedAt: string | null
}
export const mapCountRequest = (r: Record<string, unknown>): CountRequest => ({
  id: str(r.id),
  locationId: str(r.location_id),
  locationCode: strOrNull(r.location_code),
  locationName: strOrNull(r.location_name),
  path: strOrNull(r.path),
  siteId: strOrNull(r.site_id),
  requestedAt: iso(r.requested_at),
  note: strOrNull(r.note),
  requestedById: strOrNull(obj(r.requested_by).id),
  requestedByName: strOrNull(obj(r.requested_by).name ?? obj(r.requested_by).username),
  status: str(r.status) === 'DONE' ? 'DONE' : 'OPEN',
  countId: strOrNull(r.count_id),
  countedAt: iso(r.counted_at),
})
/** Counts asked of a site's places: OPEN until a count of that place (or one above it) arrives. */
export function useCountRequests(f: PageParams & { siteId?: string; locationId?: string; status?: 'OPEN' | 'DONE' | 'ALL' }, enabled = true) {
  return usePageQuery([...ck.locations, 'count-requests', f], () => getPage('/locations/count-requests', f, mapCountRequest), { enabled })
}
export function useRequestCount() {
  return useConsoleMutation<{ id: string; note?: string }>({
    mutationFn: ({ id, note }) => api.post(`/locations/${id}/count-requests`, { note: note || undefined }),
    invalidate: [ck.locations, ck.stock],
    success: 'Count requested — the keeper sees it on the phone',
  })
}
export interface Member {
  userId: string
  name: string
  roles: string[]
  hasPin: boolean
  since: string | null
}
export function useLocationMembers(id: string | null) {
  return useQuery({
    queryKey: [...ck.locations, 'members', id],
    enabled: Boolean(id),
    queryFn: () =>
      getList(`/locations/${id}/members`, undefined, (m): Member => ({
        userId: str(m.user_id ?? m.userId ?? m.id),
        name: str(m.name ?? [m.first_name, m.last_name].filter(Boolean).join(' '), str(m.username)),
        roles: arr<string>(m.roles).map(String),
        hasPin: Boolean(m.has_pin ?? m.hasPin),
        since: iso(m.created_at),
      })),
  })
}
export function useAddMember() {
  return useConsoleMutation<{ id: string; userId: string }>({
    mutationFn: ({ id, userId }) => api.post(`/locations/${id}/members`, { userId }),
    invalidate: [ck.locations],
    success: 'Added to the site team',
  })
}
export function useRemoveMember() {
  return useConsoleMutation<{ id: string; userId: string }>({
    mutationFn: ({ id, userId }) => api.delete(`/locations/${id}/members/${userId}`),
    invalidate: [ck.locations],
    success: 'Removed from the site team',
  })
}

// ============================== stock ==============================

export interface StockRow {
  key: string
  placeKind: 'LOCATION' | 'TRIP'
  locationId: string | null
  locationCode: string | null
  locationPath: string | null
  siteId: string | null
  shipmentId: string | null
  shipmentCode: string | null
  unitId: string
  unitCode: string
  unitType: string
  stream: string
  unitStatus: string
  unitHold: string
  holdReasons: string[]
  cropId: string | null
  farmerId: string | null
  farmerCode: string | null
  bags: number
  bagsOnHold: number
  kgNowG: number
  kgAtGateG: number
  firstInAt: string | null
  daysInStore: number | null
  declaredClean: boolean
  available: boolean
}
export const mapStock = (r: Record<string, unknown>): StockRow => ({
  key: `${str(r.unit_id)}:${str(r.location_id ?? r.shipment_id)}:${str(r.place_kind)}`,
  placeKind: str(r.place_kind, 'LOCATION') === 'TRIP' ? 'TRIP' : 'LOCATION',
  locationId: strOrNull(r.location_id),
  locationCode: strOrNull(r.location_code),
  locationPath: strOrNull(r.location_path),
  siteId: strOrNull(r.site_id),
  shipmentId: strOrNull(r.shipment_id),
  shipmentCode: strOrNull(r.shipment_code),
  unitId: str(r.unit_id),
  unitCode: str(r.unit_code),
  unitType: str(r.unit_type, 'LOT'),
  stream: str(r.stream, 'MAIN'),
  unitStatus: str(r.unit_status),
  unitHold: str(r.unit_hold, 'NONE'),
  holdReasons: reasons(r.hold_reason),
  cropId: strOrNull(r.crop_id),
  farmerId: strOrNull(r.farmer_id),
  farmerCode: strOrNull(r.farmer_code),
  bags: num(r.bags),
  bagsOnHold: num(r.bags_on_hold),
  kgNowG: kgToGrams0(r.kg_now as string),
  kgAtGateG: kgToGrams0(r.kg_at_gate as string),
  firstInAt: iso(r.first_in_at),
  daysInStore: numOrNull(r.days_in_store),
  declaredClean: bool(r.declared_clean),
  available: bool(r.available),
})

const holdReasonLabel = (code: string) => HOLD_REASONS.find((h) => h.value === code)?.label ?? humanize(code)

/** Status words for a stock row (F5.6 screen): available, on hold (reason), ordinary. */
export function stockStatus(r: StockRow): { status: string; label: string } {
  if (r.unitHold !== 'NONE' || r.bagsOnHold > 0)
    return { status: 'HOLD', label: `On hold${r.holdReasons.length ? ` · ${r.holdReasons.map(holdReasonLabel).join(', ')}` : ''}` }
  if (r.stream === 'CONV') return { status: 'ORDINARY', label: 'Ordinary' }
  if (r.available) return { status: 'OK', label: 'Available' }
  if (r.placeKind === 'TRIP') return { status: 'IN_TRANSIT', label: 'On a trip' }
  if (!r.declaredClean) return { status: 'PENDING', label: 'Bay not declared' }
  return { status: 'PENDING', label: 'Not put away' }
}

export interface StockFilters extends PageParams {
  cropId?: string
  stream?: string
  siteId?: string
  locationId?: string
  hold?: boolean
  available?: boolean
  placeKind?: string
  farmerId?: string
  search?: string
}
export function useStock(f: StockFilters) {
  return usePageQuery([...ck.stock, 'list', f], () => getPage('/stock', f, mapStock))
}

export interface RedTieRow {
  containerId: string
  qrPayload: string | null
  unitId: string
  unitCode: string
  unitType: string
  locationId: string | null
  locationPath: string | null
  status: string
}
export function useRedTie(siteId?: string) {
  return useQuery({
    queryKey: [...ck.stock, 'red-tie', siteId],
    queryFn: () =>
      getList('/stock/red-tie', { siteId }, (r): RedTieRow => ({
        containerId: str(r.container_id),
        qrPayload: strOrNull(r.qr_payload),
        unitId: str(r.unit_id),
        unitCode: str(r.unit_code),
        unitType: str(r.unit_type),
        locationId: strOrNull(r.location_id),
        locationPath: strOrNull(r.location_path),
        status: str(r.status),
      })),
  })
}
export interface DockRow {
  siteId: string
  siteCode: string
  unitId: string
  unitCode: string
  bags: number
  since: string | null
  stream: string
}
export function useDock(siteId?: string) {
  return useQuery({
    queryKey: [...ck.stock, 'dock', siteId],
    queryFn: () =>
      getList('/stock/dock', { siteId }, (r): DockRow => ({
        siteId: str(r.site_id),
        siteCode: str(r.site_code),
        unitId: str(r.unit_id),
        unitCode: str(r.unit_code),
        bags: num(r.bags),
        since: iso(r.since),
        stream: str(r.stream),
      })),
  })
}

export interface CustodySpan {
  locationId: string
  path: string
  level: string
  siteKind: string | null
  bayId: string | null
  from: string | null
  to: string | null
  movedById: string | null
}
export interface CustodyTrip {
  shipmentId: string
  code: string
  status: string
  vehicleClean: boolean | null
  loadedAt: string | null
  dispatchedAt: string | null
  unloadedAt: string | null
  receivedAt: string | null
}
export interface Custody {
  storageDeclared: boolean
  failingSpans: Array<{ containerId: string; reason: string; from: string | null; to: string | null }>
  bags: Array<{
    id: string
    status: string
    stream: string
    hold: string
    qrPayload: string | null
    replacesContainerId: string | null
    intervals: CustodySpan[]
    trips: CustodyTrip[]
    currentPath: string | null
    currentTripCode: string | null
  }>
}
export function useCustody(unitId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...ck.lots, 'custody', unitId],
    enabled: Boolean(unitId) && enabled,
    queryFn: () =>
      getOne(`/units/${unitId}/custody`, (r): Custody => ({
        storageDeclared: bool(r.storage_declared),
        failingSpans: arr(r.failing_spans).map((f) => ({
          containerId: str(f.containerId ?? f.container_id),
          reason: str(f.reason),
          from: iso(f.from),
          to: iso(f.to),
        })),
        bags: arr(r.bags).map((b) => {
          const cur = b.current_place ? obj(b.current_place) : null
          const trip = b.current_trip ? obj(b.current_trip) : null
          return {
            id: str(b.id),
            status: str(b.status),
            stream: str(b.stream),
            hold: str(b.hold, 'NONE'),
            qrPayload: strOrNull(b.qr_payload),
            replacesContainerId: strOrNull(b.replaces_container_id),
            intervals: arr(b.intervals).map((i) => ({
              locationId: str(i.location_id),
              path: str(i.path),
              level: str(i.level),
              siteKind: strOrNull(i.site_kind),
              bayId: strOrNull(i.bay_id),
              from: iso(i.from_ts),
              to: iso(i.to_ts),
              movedById: strOrNull(i.moved_by_id),
            })),
            trips: arr(b.trips).map((t) => ({
              shipmentId: str(t.shipment_id),
              code: str(t.code),
              status: str(t.status),
              vehicleClean: boolOrNull(t.vehicle_clean),
              loadedAt: iso(t.loaded_at),
              dispatchedAt: iso(t.dispatched_at),
              unloadedAt: iso(t.unloaded_at),
              receivedAt: iso(t.received_at),
            })),
            currentPath: cur ? strOrNull(cur.path) : null,
            currentTripCode: trip ? strOrNull(trip.code) : null,
          }
        }),
      })),
  })
}

// ============================== shipments ==============================

export interface ShipmentRow {
  id: string
  code: string
  kind: string
  status: string
  vehicleNo: string | null
  challanNo: string | null
  vehicleClean: boolean | null
  dispatchedAt: string | null
  receivedAt: string | null
  photo: string | null
  createdAt: string | null
  fromLocationId: string | null
  fromLabel: string
  toLocationId: string | null
  toLabel: string
  bags: number
  samples: number
  lots: number
  unloaded: number
  missing: number
  recheck: number
}
export const mapShipment = (r: Record<string, unknown>): ShipmentRow => ({
  id: str(r.id),
  code: str(r.code),
  kind: str(r.kind),
  status: str(r.status),
  vehicleNo: strOrNull(r.vehicle_no),
  challanNo: strOrNull(r.challan_no),
  vehicleClean: boolOrNull(r.vehicle_clean),
  dispatchedAt: iso(r.dispatched_at),
  receivedAt: iso(r.received_at),
  photo: strOrNull(r.photo_id),
  createdAt: iso(r.created_at),
  fromLocationId: strOrNull(r.from_location_id),
  fromLabel: str(r.from_path ?? r.from_name ?? r.from_code, '—'),
  toLocationId: strOrNull(r.to_location_id),
  toLabel: str(r.to_path ?? r.to_name ?? r.to_code, '—'),
  bags: num(r.bags),
  samples: num(r.samples),
  lots: num(r.lots),
  unloaded: num(r.unloaded),
  missing: num(r.missing),
  recheck: num(r.recheck),
})
export interface ShipmentFilters extends PageParams {
  status?: string
  kind?: string
  siteId?: string
  search?: string
  dateFrom?: string
  dateTo?: string
}
export function useShipments(f: ShipmentFilters) {
  return usePageQuery([...ck.shipments, 'list', f], () => getPage('/shipments', f, mapShipment))
}

export interface ManifestItem {
  itemId: string
  containerId: string | null
  sampleId: string | null
  qrPayload: string | null
  sealPayload: string | null
  unitId: string | null
  unitCode: string | null
  stream: string | null
  hold: string | null
  status: string | null
  netGateG: number | null
  tareG: number | null
  paperDay: boolean
  gateWeigherId: string | null
  loadedAt: string | null
  unloadedAt: string | null
  recheckRequired: boolean
  recheckG: number | null
  loadedById: string | null
  unloadedById: string | null
}
const mapItem = (b: Record<string, unknown>): ManifestItem => ({
  itemId: str(b.item_id),
  containerId: strOrNull(b.container_id),
  sampleId: strOrNull(b.sample_id),
  qrPayload: strOrNull(b.qr_payload),
  sealPayload: strOrNull(b.seal_payload),
  unitId: strOrNull(b.unit_id),
  unitCode: strOrNull(b.unit_code),
  stream: strOrNull(b.stream),
  hold: strOrNull(b.hold),
  status: strOrNull(b.status),
  netGateG: kgToGrams((b.net_kg_gate ?? b.net_g_gate) as string),
  tareG: kgToGrams(b.tare_kg as string),
  paperDay: bool(b.paper_day),
  gateWeigherId: strOrNull(b.gate_weigher_id),
  loadedAt: iso(b.loaded_at),
  unloadedAt: iso(b.unloaded_at),
  recheckRequired: bool(b.recheck_required),
  recheckG: kgToGrams(b.recheck_kg as string),
  loadedById: strOrNull(b.loaded_by_id),
  unloadedById: strOrNull(b.unloaded_by_id),
})

export interface ShipmentLotGroup {
  unitId: string | null
  unitCode: string | null
  stream: string | null
  unitHold: string | null
  paperDay: boolean
  bagsOnTrip: number
  bags: ManifestItem[]
}
export interface ShipmentDetail extends ShipmentRow {
  driverName: string | null
  driverPhone: string | null
  receivedById: string | null
  lotGroups: ShipmentLotGroup[]
  sampleItems: ManifestItem[]
  events: Array<{ id: string; bizStep: string; subjectType: string; subjectId: string; occurredAt: string | null; actorId: string | null; payload: Record<string, unknown> }>
  exceptions: ExceptionLite[]
}
/**
 * GET /shipments/:id — header (from_path / to_path), lots[{ unit_id, unit_code, stream, unit_hold,
 * paper_day, bags[] }], samples[], events[], exceptions[]. The list-only counts (lots, samples,
 * unloaded, missing, recheck) are not sent here, so they are worked out from the items.
 */
export const mapShipmentDetail = (r: Record<string, unknown>): ShipmentDetail => {
  const lotGroups: ShipmentLotGroup[] = arr(r.lots).map((l) => {
    const bags = arr(l.bags).map(mapItem)
    return {
      unitId: strOrNull(l.unit_id),
      unitCode: strOrNull(l.unit_code),
      stream: strOrNull(l.stream),
      unitHold: strOrNull(l.unit_hold),
      paperDay: bool(l.paper_day),
      bagsOnTrip: bags.length,
      bags,
    }
  })
  const sampleItems = arr(r.samples).map(mapItem)
  const bags = lotGroups.flatMap((l) => l.bags)
  return {
    ...mapShipment(r),
    bags: bags.length,
    lots: lotGroups.length,
    samples: sampleItems.length,
    unloaded: [...bags, ...sampleItems].filter((b) => b.unloadedAt).length,
    missing: bags.filter((b) => !b.unloadedAt && b.status === 'MISSING').length,
    recheck: bags.filter((b) => b.recheckRequired).length,
    driverName: strOrNull(r.driver_name),
    driverPhone: strOrNull(r.driver_phone),
    receivedById: strOrNull(r.received_by_id),
    lotGroups,
    sampleItems,
    events: arr(r.events).map((e) => ({
      id: str(e.id),
      bizStep: str(e.biz_step),
      subjectType: str(e.subject_type),
      subjectId: str(e.subject_id),
      occurredAt: iso(e.occurred_at),
      actorId: strOrNull(e.actor_id),
      payload: obj(e.payload),
    })),
    exceptions: arr(r.exceptions).map(mapExceptionLite),
  }
}
export function useShipment(id: string | undefined) {
  return useQuery({
    queryKey: [...ck.shipments, 'detail', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/shipments/${id}`, mapShipmentDetail),
  })
}
export interface Manifest {
  id: string
  code: string
  status: string
  vehicleNo: string | null
  fromPath: string
  toLocationId: string | null
  items: ManifestItem[]
}
export function useManifests(toLocationId: string | undefined) {
  return useQuery({
    queryKey: [...ck.shipments, 'manifests', toLocationId],
    enabled: Boolean(toLocationId),
    queryFn: () =>
      getList('/shipments/manifests', { toLocationId, status: 'IN_TRANSIT' }, (r): Manifest => ({
        id: str(r.id ?? r.shipment_id),
        code: str(r.code),
        status: str(r.status),
        vehicleNo: strOrNull(r.vehicle_no),
        fromPath: str(r.from_path, '—'),
        toLocationId: strOrNull(r.to_location_id),
        items: arr(r.items).map(mapItem),
      })),
  })
}

// ============================== samples ==============================

export interface SampleRow {
  id: string
  kind: string
  status: string
  subjectType: string
  subjectId: string
  unitCode: string | null
  sealPayload: string | null
  collectedAt: string | null
  weightG: number | null
  retainUntil: string | null
  locationId: string | null
  locationPath: string | null
  shipmentId: string | null
  labOrgId: string | null
  labName: string | null
  sentAt: string | null
  panelRequested: string | null
  dispatchRef: string | null
  where: string
}
export const mapSample = (r: Record<string, unknown>): SampleRow => ({
  id: str(r.id),
  kind: str(r.kind),
  status: str(r.status),
  subjectType: str(r.subject_type),
  subjectId: str(r.subject_id),
  unitCode: strOrNull(r.unit_code),
  sealPayload: strOrNull(r.seal_payload),
  collectedAt: iso(r.collected_at),
  weightG: numOrNull(r.weight_g),
  retainUntil: iso(r.retain_until),
  locationId: strOrNull(r.location_id),
  locationPath: strOrNull(r.location_path),
  shipmentId: strOrNull(r.shipment_id),
  labOrgId: strOrNull(r.lab_org_id),
  labName: strOrNull(r.lab_name),
  sentAt: iso(r.sent_at),
  panelRequested: strOrNull(r.panel_requested),
  dispatchRef: strOrNull(r.dispatch_ref),
  where: str(r.where, 'NO_PLACE'),
})
export const SAMPLE_WHERE_LABEL: Record<string, string> = {
  IN_STORE: 'In the sample store',
  AT_SITE: 'At a site',
  ON_TRIP: 'On a trip',
  SENT: 'Sent to the lab',
  NO_PLACE: 'No place recorded',
}
export function useSamples(f: PageParams & { unitId?: string; kind?: string; status?: string; siteId?: string }) {
  return usePageQuery([...ck.samples, 'list', f], () => getPage('/samples', f, mapSample))
}
export function useDispatchSample() {
  return useConsoleMutation<{ id: string; labOrgId: string; panelRequested: string; sentAt: string; dispatchRef?: string }>({
    mutationFn: ({ id, ...body }) => api.post(`/samples/${id}/dispatch`, body),
    invalidate: [ck.samples, ck.lots],
    success: 'Sample sent to the lab',
    // The dialog shows the refusal inline (dispatchErrorMessage), so no toast.
    onError: () => true,
  })
}

// ============================== declarations ==============================

export interface BayDeclarationRow {
  id: string
  bayId: string
  bayCode: string
  bayPath: string
  formCode: string
  formVersion: number
  answers: Record<string, unknown>
  signedById: string | null
  signedByName: string | null
  signedAt: string | null
  validTo: string | null
  photo: string | null
  photoArrived: boolean
  clean: boolean | null
  latest: boolean
  inForce: boolean
}
export const mapDeclaration = (r: Record<string, unknown>): BayDeclarationRow => ({
  id: str(r.id),
  bayId: str(r.bay_id),
  bayCode: str(r.bay_code),
  bayPath: str(r.bay_path),
  formCode: str(r.form_code),
  formVersion: num(r.form_version, 1),
  answers: obj(r.answers),
  signedById: strOrNull(r.signed_by_id),
  signedByName: strOrNull(r.signed_by_name),
  signedAt: iso(r.signed_at),
  validTo: strOrNull(r.valid_to),
  photo: strOrNull(r.photo_id),
  photoArrived: bool(r.photo_arrived),
  clean: boolOrNull(r.clean),
  latest: bool(r.latest),
  inForce: bool(r.in_force),
})
export type BayDeclarationFilters = PageParams & { siteId?: string; locationId?: string }
export function useBayDeclarations(f: BayDeclarationFilters) {
  return usePageQuery([...ck.declarations, 'list', f], () => getPage('/declarations/bays', f, mapDeclaration))
}
/** Every declaration matching the filters (auditor export), 500 a page. */
export async function fetchAllBayDeclarations(f: Omit<BayDeclarationFilters, 'page' | 'limit'>) {
  const out: BayDeclarationRow[] = []
  for (let page = 1; ; page++) {
    const p = await getPage('/declarations/bays', { ...f, page, limit: 500 }, mapDeclaration)
    out.push(...p.rows)
    if (page >= p.totalPages || p.rows.length === 0) return out
  }
}
export interface OutOfDateBay {
  bayId: string
  bayCode: string
  path: string
  siteId: string | null
  brandBags: number
  state: string
  validTo: string | null
}
export function useOutOfDateBays(siteId?: string) {
  return useQuery({
    queryKey: [...ck.declarations, 'out-of-date', siteId],
    queryFn: () =>
      getList('/declarations/bays/out-of-date', { siteId }, (r): OutOfDateBay => ({
        bayId: str(r.bay_id),
        bayCode: str(r.bay_code),
        path: str(r.path),
        siteId: strOrNull(r.site_id),
        brandBags: num(r.brand_bags),
        state: str(r.state),
        validTo: strOrNull(obj(r.declaration).valid_to),
      })),
  })
}
export const DECLARATION_STATE_LABEL: Record<string, string> = {
  NONE: 'Never declared',
  EXPIRED_OR_OLD_FORM: 'Expired or old form',
  NOT_CLEAN: 'Declared not clean',
  EXPIRING: 'Expiring soon',
  OK: 'Declared clean',
}

/** A declaration form as kept in uc-core code (GET /declaration-forms/:formCode, current version). */
export interface DeclarationFormQuestion {
  key: string
  type: string
  labelEn: string
  labelHi: string | null
  required: boolean
  riskyAnswers: unknown[]
  requiredWhen: { key: string; equals: unknown } | null
}
export interface DeclarationFormDef {
  formCode: string
  version: number
  validDays: number | null
  questions: DeclarationFormQuestion[]
}
export const mapDeclarationForm = (r: Record<string, unknown>): DeclarationFormDef => ({
  formCode: str(r.form_code),
  version: num(r.version, 1),
  validDays: numOrNull(r.valid_days),
  questions: arr(r.questions).map((q) => ({
    key: str(q.key),
    type: str(q.type),
    labelEn: str(q.label_en, str(q.key)),
    labelHi: strOrNull(q.label_hi),
    required: bool(q.required),
    riskyAnswers: arr<unknown>(q.risky_answers),
    requiredWhen: q.required_when ? { key: str(obj(q.required_when).key), equals: obj(q.required_when).equals } : null,
  })),
})
export function useDeclarationForm(formCode: string | null) {
  return useQuery({
    queryKey: [...ck.declarations, 'form', formCode],
    enabled: Boolean(formCode),
    staleTime: 30 * 60_000,
    queryFn: () => getOne(`/declaration-forms/${encodeURIComponent(formCode ?? '')}`, mapDeclarationForm),
  })
}

/** Find a lot by code / tag / farmer across the store (GET /stock/lookup; 404 when none). */
export interface LotLookup {
  id: string
  code: string
  status: string
  stream: string
  hold: string
  holdReasons: string[]
  bagCount: number | null
  netG: number | null
  bagsByStatus: Record<string, number>
  places: Array<{
    placeKind: 'LOCATION' | 'TRIP'
    locationPath: string | null
    shipmentId: string | null
    shipmentCode: string | null
    bags: number
    bagsOnHold: number
    kgNowG: number
  }>
}
export function stockLookup(q: { code?: string; tag?: string; farmerId?: string }) {
  return getList('/stock/lookup', q, (r): LotLookup => ({
    id: str(r.id ?? r.unit_id),
    code: str(r.code ?? r.unit_code),
    status: str(r.status),
    stream: str(r.stream),
    hold: str(r.hold, 'NONE'),
    holdReasons: reasons(r.hold_reason),
    bagCount: numOrNull(r.bag_count),
    netG: kgToGrams(r.net_kg as string),
    bagsByStatus: Object.fromEntries(Object.entries(obj(r.bags_by_status)).map(([k, v]) => [k, num(v)])),
    places: arr(r.places).map((p) => ({
      placeKind: str(p.place_kind) === 'TRIP' ? 'TRIP' : 'LOCATION',
      locationPath: strOrNull(p.location_path),
      shipmentId: strOrNull(p.shipment_id),
      shipmentCode: strOrNull(p.shipment_code),
      bags: num(p.bags),
      bagsOnHold: num(p.bags_on_hold),
      kgNowG: kgToGrams0(p.kg_now as string),
    })),
  }))
}
